import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, TablePagination, Button, Box,
  LinearProgress, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import { getUploadHistoryApi, downloadUploadedFileApi, downloadTemplateApi } from '../../api/uploads';
import { getPublishersApi } from '../../api/auth';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function AdminUploadHistory() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});

  const fetchHistory = useCallback(async () => {
    const res = await getUploadHistoryApi({ page: page + 1, page_size: rowsPerPage, search, ...filters });
    setHistory(res.data.results || res.data);
    setTotal(res.data.count || res.data.length);
  }, [page, rowsPerPage, search, filters]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => {
    getPublishersApi({ page_size: 200 }).then((r) => setPublishers(r.data.results || r.data));
  }, []);

  const handleDownload = async (id, name) => {
    const res = await downloadUploadedFileApi(id);
    downloadBlob(res.data, name);
  };

  const handleDownloadTemplate = async () => {
    const res = await downloadTemplateApi();
    downloadBlob(res.data, 'book_upload_template.xlsx');
  };

  const historyFilters = [
    { key: 'publisher', label: 'Publisher', type: 'select', options: publishers.map(p => ({ value: p.id, label: p.company_name || p.name })) },
    { key: 'upload_source', label: 'Source', type: 'select', options: [{ value: 'SELF', label: 'Self' }, { value: 'ADMIN', label: 'Admin' }] },
  ];

  return (
    <Layout title="Upload History">
      <PageHeader
        title="Upload History"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Upload History' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
              Download Template
            </Button>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => navigate('/admin/upload')}>
              Upload Books
            </Button>
          </Box>
        }
      />
      <Card>
        <CardContent>
          <SearchFilterBar
            searchPlaceholder="Search by file name, publisher..."
            filters={historyFilters}
            onSearch={setSearch}
            onFilter={setFilters}
          />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Publisher</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Uploaded By</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Success</TableCell>
                <TableCell>Failed</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} hover>
                  <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.original_file_name}</TableCell>
                  <TableCell>{h.publisher_name}</TableCell>
                  <TableCell>{h.publisher_mobile}</TableCell>
                  <TableCell>{h.uploaded_by_name}</TableCell>
                  <TableCell>
                    <Chip label={h.upload_source} size="small" color={h.upload_source === 'ADMIN' ? 'secondary' : 'primary'} />
                  </TableCell>
                  <TableCell>{new Date(h.upload_date).toLocaleDateString()}</TableCell>
                  <TableCell>{h.total_records}</TableCell>
                  <TableCell><Typography variant="body2" color="success.main">{h.success_count}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="error.main">{h.failed_count}</Typography></TableCell>
                  <TableCell>
                    <Tooltip title="Download file">
                      <IconButton size="small" onClick={() => handleDownload(h.id, h.original_file_name)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </CardContent>
      </Card>
    </Layout>
  );
}
